import {loadExternalScript, resetExternalScript} from './externalScriptLoader';

let privacyWebSdkScriptLoad: Promise<void> | null = null;
const PRIVACY_WEB_SDK_PRELOAD_ID = 'privacy-web-sdk-preload';

const addPrivacyWebSdkPreconnect = (): void => {
  try {
    const origin = new URL(window.REACT_APP_PRIVACY_URL).origin;
    if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    document.head.appendChild(link);
  } catch {
    // Ignore invalid runtime configuration and let the script request report the failure.
  }
};

const addPrivacyWebSdkPreload = (): void => {
  try {
    const scriptUrl = new URL(
      `${window.REACT_APP_PRIVACY_URL}/${window.REACT_APP_PRIVACY_SCRIPT_NAME}`,
      window.location.origin
    ).href;
    if (document.getElementById(PRIVACY_WEB_SDK_PRELOAD_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = PRIVACY_WEB_SDK_PRELOAD_ID;
    link.rel = 'preload';
    link.as = 'script';
    link.href = scriptUrl;
    document.head.appendChild(link);
  } catch {
    // Ignore invalid runtime configuration and let the script request report the failure.
  }
};

export const resetPrivacyWebSdkScript = (): void => {
  privacyWebSdkScriptLoad = null;
  const scriptSrc = `${window.REACT_APP_PRIVACY_URL}/${window.REACT_APP_PRIVACY_SCRIPT_NAME}`;
  resetExternalScript(scriptSrc, false);
  delete window.PrivacyWebSDK;
};

export const loadPrivacyWebSdkScript = (): Promise<void> => {
  addPrivacyWebSdkPreconnect();
  addPrivacyWebSdkPreload();

  if (window.PrivacyWebSDK) {
    return Promise.resolve();
  }

  if (privacyWebSdkScriptLoad) {
    return privacyWebSdkScriptLoad;
  }

  const scriptSrc = `${window.REACT_APP_PRIVACY_URL}/${window.REACT_APP_PRIVACY_SCRIPT_NAME}`;

  // Derived from REACT_APP_PRIVACY_URL itself, not a separate allowlist config — unlike PPIA's
  // multiple flow URLs, this SDK only ever has one configured host per environment.
  let scriptHost = '';
  let expectedHost = '';
  try {
    scriptHost = new URL(scriptSrc).hostname;
    expectedHost = new URL(window.REACT_APP_PRIVACY_URL).hostname;
  } catch {
    return Promise.reject(new Error('Failed to load the Privacy SDK script.'));
  }
  if (scriptHost !== expectedHost) {
    return Promise.reject(new Error('Privacy SDK host is not allowed.'));
  }

  privacyWebSdkScriptLoad = loadExternalScript(scriptSrc, {
    errorMessage: 'Failed to load the Privacy SDK script.',
    cacheResolved: false,
    removeOnError: false,
  }).catch((error) => {
    privacyWebSdkScriptLoad = null;
    throw error;
  });

  return privacyWebSdkScriptLoad;
};

export const preloadPrivacyWebSdk = (): void => {
  if (!window.REACT_APP_PRIVACY_URL || !window.REACT_APP_PRIVACY_SCRIPT_NAME) {
    return;
  }

  addPrivacyWebSdkPreconnect();
  addPrivacyWebSdkPreload();
};
